/**
 * The softphone's state machine (#207 step 2): what the phone is doing,
 * independent of how it is drawn and of SIP.js.
 *
 * On mount it asks intake who this browser is on the voice server and
 * registers as them; on unmount it leaves. Between, it holds at most one
 * call — an Operator's phone is one line — and the five things a person
 * can do to it: dial, answer, hang up, mute, press a key.
 *
 * Both collaborators are injected so a test can hand in a fake stack and
 * a canned credential: `stack` is the SIP side, `load` the API read.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "../../../shared/api-client";
import { fetchSoftphone, type SoftphoneCredentials } from "./softphone-api";
import type { CallDirection, CallState, SipCall, SipStack } from "./sip-stack";
import { createRinger, type Ringer } from "./ringer";

/**
 * - `off`: not yet known, this deployment has no voice server, it is not
 *   on for this Company, or the person's endpoint could not be written.
 *   Nothing to do here; `message` says why. Where every hook starts, so
 *   the bar shows no handset until the server has said there is one.
 * - `connecting`: credentials fetched, registration under way, or lost
 *   and being re-made.
 * - `ready`: registered; calls can be placed and will arrive.
 * - `error`: something failed that a retry might fix.
 */
export type PhoneStatus = "off" | "connecting" | "ready" | "error";

export interface ActiveCall {
  direction: CallDirection;
  remote: string;
  state: CallState;
  /** Set when the call is answered; the screen counts from it. */
  answeredAt: number | null;
  muted: boolean;
  /** Why it ended, once it has. */
  endReason: string;
}

export interface SoftphoneState {
  status: PhoneStatus;
  message: string;
  /** Who this browser is registered as; empty until the credential is read. */
  username: string;
  call: ActiveCall | null;
}

export interface SoftphoneActions {
  dial(target: string): Promise<void>;
  answer(): Promise<void>;
  hangup(): Promise<void>;
  toggleMute(): void;
  sendDTMF(tone: string): void;
  /** Forget an ended call so the dial screen comes back. */
  clearEnded(): void;
  /** Try the credential read and registration again after an error. */
  retry(): void;
}

export interface UseSoftphoneOptions {
  stack: SipStack;
  load?: (signal?: AbortSignal) => Promise<SoftphoneCredentials>;
  ringer?: Ringer;
}

/**
 * Digits and a leading plus; spaces, dashes and brackets a person types
 * are not part of a number. A bare word (`echo`) is a named extension on
 * the server and goes through as typed, which is how the path is proved
 * without ringing anybody.
 */
export function normaliseDialTarget(raw: string): string {
  const trimmed = raw.trim();
  if (/^[a-z][a-z0-9_-]*$/i.test(trimmed)) return trimmed.toLowerCase();
  const plus = trimmed.startsWith("+") ? "+" : "";
  const digits = trimmed.replace(/[^0-9*#]/g, "");
  return plus + digits;
}

const initial: SoftphoneState = { status: "off", message: "", username: "", call: null };

export function useSoftphone({ stack, load = fetchSoftphone, ringer }: UseSoftphoneOptions): [SoftphoneState, SoftphoneActions] {
  const [state, setState] = useState<SoftphoneState>(initial);
  const [attempt, setAttempt] = useState(0);
  const callRef = useRef<SipCall | null>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const ringerRef = useRef<Ringer | null>(ringer ?? null);
  if (!ringerRef.current) ringerRef.current = createRinger();
  // The credential read is held in a ref so a caller handing in a fresh
  // function each render does not restart registration each render — the
  // effect below would otherwise cancel and re-run itself without end.
  const loadRef = useRef(load);
  loadRef.current = load;

  const adopt = useCallback((call: SipCall) => {
    // One line: a second call while one is up is declined, not juggled.
    if (callRef.current && callRef.current.state !== "ended") {
      call.hangup().catch(() => {});
      return;
    }
    unsubscribeRef.current?.();
    callRef.current = call;
    const snapshot = (s: CallState): ActiveCall => ({
      direction: call.direction,
      remote: call.remote,
      state: s,
      answeredAt: null,
      muted: false,
      endReason: call.endReason,
    });
    setState((prev) => ({ ...prev, call: snapshot(call.state) }));
    if (call.direction === "inbound") ringerRef.current?.start();
    unsubscribeRef.current = call.subscribe((s) => {
      if (s !== "ringing") ringerRef.current?.stop();
      setState((prev) => {
        if (!prev.call) return prev;
        return {
          ...prev,
          call: {
            ...prev.call,
            state: s,
            answeredAt: s === "answered" && !prev.call.answeredAt ? Date.now() : prev.call.answeredAt,
            endReason: call.endReason,
          },
        };
      });
    });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    (async () => {
      let creds: SoftphoneCredentials;
      try {
        creds = await loadRef.current(controller.signal);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 503) {
          setState((prev) => ({ ...prev, status: "off", message: err.message }));
        } else if (err instanceof ApiError) {
          setState((prev) => ({ ...prev, status: "error", message: err.message }));
        } else {
          setState((prev) => ({ ...prev, status: "error", message: "Could not reach intake" }));
        }
        return;
      }
      if (cancelled) return;
      setState((prev) => ({ ...prev, status: "connecting", message: "", username: creds.username }));
      try {
        await stack.connect(creds, {
          onRegistered: () => {
            if (!cancelled) setState((prev) => ({ ...prev, status: "ready", message: "" }));
          },
          onUnregistered: (reason) => {
            if (!cancelled) setState((prev) => ({ ...prev, status: "connecting", message: reason }));
          },
          onIncoming: (call) => {
            if (!cancelled) adopt(call);
          },
        });
      } catch (err) {
        if (cancelled) return;
        setState((prev) => ({
          ...prev,
          status: "error",
          message: err instanceof Error && err.message ? err.message : "Could not connect to the voice server",
        }));
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
      ringerRef.current?.stop();
      unsubscribeRef.current?.();
      unsubscribeRef.current = null;
      callRef.current = null;
      void stack.disconnect();
    };
  }, [stack, attempt, adopt]);

  const dial = useCallback(
    async (raw: string) => {
      const target = normaliseDialTarget(raw);
      if (!target) return;
      if (callRef.current && callRef.current.state !== "ended") return;
      try {
        const call = await stack.call(target);
        adopt(call);
      } catch (err) {
        setState((prev) => ({
          ...prev,
          call: {
            direction: "outbound",
            remote: target,
            state: "ended",
            answeredAt: null,
            muted: false,
            endReason: err instanceof Error && err.message ? err.message : "Call failed",
          },
        }));
      }
    },
    [stack, adopt],
  );

  const answer = useCallback(async () => {
    const call = callRef.current;
    if (!call || call.direction !== "inbound" || call.state !== "ringing") return;
    ringerRef.current?.stop();
    await call.answer();
  }, []);

  const hangup = useCallback(async () => {
    const call = callRef.current;
    if (!call || call.state === "ended") return;
    ringerRef.current?.stop();
    await call.hangup();
  }, []);

  const toggleMute = useCallback(() => {
    const call = callRef.current;
    if (!call || call.state !== "answered") return;
    setState((prev) => {
      if (!prev.call) return prev;
      const muted = !prev.call.muted;
      call.setMuted(muted);
      return { ...prev, call: { ...prev.call, muted } };
    });
  }, []);

  const sendDTMF = useCallback((tone: string) => {
    const call = callRef.current;
    if (!call || call.state !== "answered") return;
    call.sendDTMF(tone);
  }, []);

  const clearEnded = useCallback(() => {
    if (callRef.current && callRef.current.state !== "ended") return;
    unsubscribeRef.current?.();
    unsubscribeRef.current = null;
    callRef.current = null;
    setState((prev) => (prev.call?.state === "ended" ? { ...prev, call: null } : prev));
  }, []);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  return [state, { dial, answer, hangup, toggleMute, sendDTMF, clearEnded, retry }];
}
